import { findSchedulingUrl } from './find-scheduling-url';

import { VETSPIRE_SCHEDULING_KEYWORD } from '@src/constants';
import {
	BffBrandType,
	BffFacility,
	CheckFacilitySupportByIdDocument,
	NamedAttribute,
} from '@src/lib/__generated/beffe/sdk';
import type { Location, LocationAttribute } from '@src/lib/__generated/vetspire/sdk';
import { getBeffeGraphQlClient } from '@src/lib/client';

/**
 * Maps a BEFFE facilityById response to the Vetspire Location shape expected by the facility store
 * and downstream consumers (DetermineScheduleType, findSchedulingUrl, etc.).
 *
 * Behavioral gap: BEFFE workingHours uses date-specific entries rather than day-of-week ranges,
 * so locationHours is set to null. This means isFacilityOpen (used in ReasonForVisit.helpers)
 * will always return false (facility treated as always closed for the contact-block).
 */
function mapBeffeFacilityToLocation(facility: BffFacility): Location {
	// Remap flat BEFFE NamedAttribute → Vetspire LocationAttribute shape so that
	// findSchedulingUrl and DetermineScheduleType (which read attribute.customAttribute.name) work unchanged.
	const attributes: LocationAttribute[] = (facility.attributes ?? [])
		.filter((attr): attr is NamedAttribute => attr != null)
		.map((attr) => ({
			customAttribute: { id: '', name: attr.name ?? '' },
			value: attr.value ?? null,
		}));

	return {
		id: facility.id,
		displayName: facility.displayName ?? facility.name ?? '',
		phoneNumber: facility.phoneNumber ?? null,
		latitude: facility.location?.latitude ?? null,
		longitude: facility.location?.longitude ?? null,
		timezone: facility.location?.timezoneIANA ?? facility.location?.timeZone ?? null,
		addressFields: {
			line1: facility.address?.address1 ?? null,
			line2: facility.address?.address2 ?? null,
			city: facility.address?.city ?? null,
			state: facility.address?.stateCode ?? null,
			postalCode: facility.address?.zipCode ?? null,
			text: null,
		},
		addressString: null,
		attributes,
		locationHours: null,
	} as unknown as Location;
}

export default async function checkFacilitySupport(facilityID: string, brand: BffBrandType, beffeUrl: string) {
	const response = await getBeffeGraphQlClient(beffeUrl)
		.query<{ facilityById: BffFacility | null }>(CheckFacilitySupportByIdDocument, { brand, id: facilityID })
		.toPromise();

	const facility = response.data?.facilityById;

	if (!facility) {
		console.error(`checkFacilitySupport(): facility ${facilityID} not found.`);
		return { supported: false };
	}

	if (!facility.attributes?.length) {
		console.error(`checkFacilitySupport(): no attributes on facility: ${facilityID}.`);
		return { supported: false };
	}

	const location = mapBeffeFacilityToLocation(facility);
	const schedulingURL = findSchedulingUrl(location.attributes as LocationAttribute[]);

	if (!schedulingURL) {
		console.error(`checkFacilitySupport(): facility ${facilityID} does not have schedulingURL attribute.`);
		return { supported: false };
	}

	return {
		supported: schedulingURL.includes(VETSPIRE_SCHEDULING_KEYWORD),
		location,
	};
}
